import React from 'react';
import { Link } from 'react-router-dom';
import { getWhatsAppUrl, getEmailUrl } from '../utils/contact';
import ProductCard from '../components/ProductCard';
import FAQAccordion from '../components/FAQAccordion';
import { products } from '../data/products';
import { faqs } from '../data/faqs';

const Home = () => {
  return (
    <div className="home">
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Technology for Pharmacy, Regulation & Industry</h1>
              <p>Empowering pharmaceutical professionals with specialized software solutions for education, regulatory compliance, and workflow automation.</p>
              
              <div className="hero-ctas">
                <Link to="/products" className="btn btn-primary">Explore Products</Link>
                <Link to="/contact" className="btn btn-secondary">Get in Touch</Link>
              </div>
            </div>
            <div className="hero-visual">
              <img
                src="/images/hero-team-lagos.jpg"
                alt="Nigerian professionals collaborating on a laptop"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="who-we-serve section-surface">
        <div className="container">
          <h2>Who We Serve</h2>
          <p className="section-intro">
            Supporting stakeholders across the pharmacy and regulatory ecosystem with tools that scale from individual learners to enterprise compliance teams.
          </p>
          <div className="cards-grid">
            <div className="card">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              </div>
              <h3>Students & Early Professionals</h3>
              <p>Comprehensive learning tools and structured programs designed for pharmaceutical education and career development.</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <h3>Faculty & Institutions</h3>
              <p>Advanced management systems and curriculum delivery platforms for educational excellence.</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              </div>
              <h3>Pharma/Manufacturing Teams</h3>
              <p>Streamlined dossier compilation and regulatory documentation tools for efficient workflows.</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h3>Regulatory/Compliance Clients</h3>
              <p>Regulatory guidance and compliance workflows tailored for the African pharmaceutical landscape.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="what-we-do">
        <div className="container">
          <div className="split-section">
            <div>
              <h2>What We Do</h2>
              <p className="section-intro left">
                Combining pharmacy expertise with modern software engineering to deliver practical tools, comprehensive training, and dedicated support.
              </p>
              <div className="pillars-grid">
                <div className="pillar">
                  <div className="pillar-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                    </svg>
                  </div>
                  <h3>Train</h3>
                  <p>Structured cohorts and comprehensive learning paths for students and professional teams.</p>
                </div>
                <div className="pillar">
                  <div className="pillar-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                  </div>
                  <h3>Build</h3>
                  <p>Custom portals, intuitive dashboards, and seamless integrations for your workflows.</p>
                </div>
                <div className="pillar">
                  <div className="pillar-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M12 1v6m0 6v6m5.2-13.2l-4.2 4.2m0 6l4.2 4.2M23 12h-6m-6 0H1m18.2 5.2l-4.2-4.2m0-6l4.2-4.2"/>
                    </svg>
                  </div>
                  <h3>Automate</h3>
                  <p>Digitize forms, streamline approvals, and automate document assembly at scale.</p>
                </div>
                <div className="pillar">
                  <div className="pillar-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3>Comply</h3>
                  <p>Regulatory tooling precisely aligned to African pharmaceutical realities.</p>
                </div>
              </div>
            </div>
            <div className="section-visual">
              <img
                src="/images/tech-lagos.jpg"
                alt="Nigerian professional working on a laptop"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="featured-products section-surface">
        <div className="container">
          <h2>Featured Products</h2>
          <div className="products-grid">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="why-us">
        <div className="container">
          <div className="split-section">
            <div className="section-visual">
              <img
                src="/images/health-lagos.jpg"
                alt="Nigerian doctor reviewing patient notes"
                loading="lazy"
              />
            </div>
            <div>
              <h2>Why ProgrammoCeuticals</h2>
              <div className="cards-grid">
                <div className="card">
                  <h3>Pharmacy + Engineering Expertise</h3>
                  <p>Built by a licensed pharmacist and software engineer who understands the real workflow pain points.</p>
                </div>
                <div className="card">
                  <h3>Designed for Local Realities</h3>
                  <p>Tools mapped to regulatory and regional requirements, with flexibility for internal SOPs.</p>
                </div>
                <div className="card">
                  <h3>Secure by Design</h3>
                  <p>Privacy-first architecture with offline-ready workflows where required.</p>
                </div>
                <div className="card">
                  <h3>Scales With You</h3>
                  <p>From individual users to faculty-wide systems, our stack grows with your needs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="stats section-surface">
        <div className="container">
          <h2>Outcomes That Matter</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>40%+</h3>
              <p>Faster dossier preparation with structured templates.</p>
            </div>
            <div className="stat-card">
              <h3>3x</h3>
              <p>Improved training consistency across cohorts.</p>
            </div>
            <div className="stat-card">
              <h3>24/7</h3>
              <p>Access to regulatory guidance and learning tools.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="use-cases">
        <div className="container">
          <h2>Use Cases</h2>
          <p className="section-intro">
            Examples of how teams are deploying ProgrammoCeuticals today.
          </p>
          <div className="cards-grid">
            <div className="card">
              <h3>Regulatory Submission Readiness</h3>
              <p>Coordinate dossier elements across departments and maintain a clear audit trail.</p>
            </div>
            <div className="card">
              <h3>Faculty Results Management</h3>
              <p>Securely capture, process, and publish student results with transparency.</p>
            </div>
            <div className="card">
              <h3>Compliance Training</h3>
              <p>Deliver standardized training for industry teams with measurable outcomes.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonials section-surface">
        <div className="container">
          <h2>Pilot Partners & Early Adopters</h2>
          <div className="cards-grid">
            <div className="card">
              <p>“The dossier workflow reduced our manual compilation time and made compliance reviews smoother.”</p>
              <strong>Regulatory Affairs Lead</strong>
            </div>
            <div className="card">
              <p>“Our cohort engagement improved because learners could track progress in one place.”</p>
              <strong>Faculty Coordinator</strong>
            </div>
            <div className="card">
              <p>“ProgrammoCeuticals feels built for our reality, not a generic platform.”</p>
              <strong>Industry Partner</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      <section className="final-cta section-surface">
        <div className="container">
          <h2>Need help choosing the right tool?</h2>
          <p className="section-intro">
            Tell us about your goals and we will point you to the right product or service.
          </p>
          <div className="cta-buttons">
            <a href={getWhatsAppUrl('I need help choosing the right ProgrammoCeuticals tool')} className="btn btn-primary">WhatsApp</a>
            <a href="tel:+2349033782254" className="btn btn-secondary">Call</a>
            <a href={getEmailUrl('Help choosing the right tool')} className="btn btn-tertiary">Email</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
